import offlineDb from './db';

function parseQueryParams(queryString) {
  const params = {};
  if (!queryString) return params;
  const searchParams = new URLSearchParams(queryString);
  for (const [key, value] of searchParams.entries()) {
    params[key] = value;
  }
  return params;
}

function parseEndpoint(endpoint) {
  const [path, queryString] = endpoint.split('?');
  const params = parseQueryParams(queryString);
  return { path, params };
}

function extractId(path, prefix) {
  const match = path.match(new RegExp(`^${prefix}/([^/]+)$`));
  return match ? match[1] : null;
}

function normalizeDob(value) {
  if (!value) return null;
  const s = String(value);
  // Already YYYY-MM-DD
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  // MM/DD/YYYY
  const us = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (us) return `${us[3]}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}`;
  const parsed = new Date(s);
  if (!isNaN(parsed)) {
    return `${parsed.getUTCFullYear()}-${String(parsed.getUTCMonth() + 1).padStart(2, '0')}-${String(parsed.getUTCDate()).padStart(2, '0')}`;
  }
  return null;
}

class CacheReader {
  constructor() {
    // Last partially-served calendar range (requested range only partly
    // inside the cached window) — surfaced by OfflineStatusBar.
    this.lastPartial = null;
    this._partialSubscribers = new Set();
  }

  subscribePartial(callback) {
    this._partialSubscribers.add(callback);
    return () => this._partialSubscribers.delete(callback);
  }

  _emitPartial(partial) {
    this.lastPartial = partial;
    for (const cb of this._partialSubscribers) {
      try { cb(partial); } catch { /* ignore */ }
    }
  }

  async resolveFromCache(endpoint) {
    try {
      const { path, params } = parseEndpoint(endpoint);

      // Shape-faithful exact-key echoes.
      if (path === '/organization_location') {
        const row = await offlineDb.publicCache.get(endpoint);
        return row ? row.data : null;
      }

      return await this._resolve(path, params);
    } catch (e) {
      console.error('[CacheReader] Error resolving from cache:', e);
      return null;
    }
  }

  async _resolve(path, params) {
    // Single-record endpoints (by ID)
    const clinicalNoteId = extractId(path, '/clinical_note');
    if (clinicalNoteId) return offlineDb.clinicalNotes.get(clinicalNoteId);

    const encounterId = extractId(path, '/encounter');
    if (encounterId) return offlineDb.encounters.get(encounterId);

    const patientId = extractId(path, '/patient');
    if (patientId) return offlineDb.patients.get(patientId);

    const appointmentId = extractId(path, '/appointment');
    if (appointmentId) return offlineDb.appointments.get(appointmentId);

    const caseId = extractId(path, '/case');
    if (caseId) {
      const id = isNaN(caseId) ? caseId : parseInt(caseId, 10);
      return offlineDb.cases.get(id);
    }

    const templateId = extractId(path, '/templates');
    if (templateId) return offlineDb.templates.get(templateId);

    // List endpoints with filters
    if (path === '/clinical_note') {
      return this._queryClinicalNotes(params);
    }

    if (path === '/encounter') {
      return this._queryEncounters(params);
    }

    if (path === '/appointment') {
      return this._queryAppointments(params);
    }

    if (path === '/case') {
      return this._queryCases(params);
    }

    if (path === '/plan-of-care') {
      return this._queryPlansOfCare(params);
    }

    if (path === '/templates') {
      return this._queryTemplates(params);
    }

    if (path === '/form-responses') {
      return this._queryFormResponses(params);
    }

    if (path === '/form-generated-reports') {
      return this._queryFormReports(params);
    }

    if (path === '/user') {
      return this._queryOrganizationUsers(params);
    }

    if (path === '/appointment_type') {
      return this._queryAppointmentTypes(params);
    }

    if (path === '/billing-rules') {
      return this._queryBillingRules(params);
    }

    if (path === '/intervention-catalog') {
      return this._queryInterventionCatalog(params);
    }

    if (path === '/calendar-appointments') {
      return this._queryCalendarFeed(params);
    }

    if (path === '/patient-with-pagination') {
      return this._queryPatientSearch(params);
    }

    const organizationId = extractId(path, '/organization');
    if (organizationId) {
      return offlineDb.organizations.get(organizationId);
    }

    if (path === '/flow-sheet-entry') {
      return this._queryFlowSheetEntries(params);
    }

    const flowEntryId = extractId(path, '/flow-sheet-entry');
    if (flowEntryId) return offlineDb.flowSheetEntries.get(flowEntryId);

    if (path === '/flow-sheet-entry-latest') {
      return this._queryFlowSheetLatest(params);
    }

    if (path === '/flow-sheet-item-catalog') {
      return this._queryFlowSheetCatalog(params);
    }

    if (path === '/user_solution') {
      return this._querySessionRow('solutions', (row) =>
        row.organizationId === params.organization_id && row.userId === params.user_id);
    }

    const orgRoleId = extractId(path, '/organization-role');
    if (orgRoleId) {
      return this._querySessionRow('orgRole', (row) => String(row.roleId) === String(orgRoleId));
    }

    return null;
  }

  async _querySessionRow(key, matches) {
    const row = await offlineDb.offlineSession.get(key);
    if (!row || !matches(row)) return null;
    return row.data;
  }

  // Six-key /calendar-appointments feed, same shape as the server so the
  // API layer's stitching runs unchanged. Range queries outside the cached
  // window return null (→ visible offline error); partial overlaps serve
  // the intersection and record lastPartial for the status bar.
  async _queryCalendarFeed(params) {
    let appts;

    if (params.appointment_id) {
      const one = await offlineDb.appointments.get(params.appointment_id);
      appts = one ? [one] : [];
    } else if (params.start_date) {
      const windowMeta = await offlineDb.cacheMeta.get('calendarWindow');
      const reqStart = params.start_date;
      const reqEnd = params.end_date || params.start_date;

      if (windowMeta?.start) {
        const servedStart = reqStart > windowMeta.start ? reqStart : windowMeta.start;
        const servedEnd = reqEnd < windowMeta.end ? reqEnd : windowMeta.end;
        if (servedStart > servedEnd) return null; // fully outside the window
        if (servedStart !== reqStart || servedEnd !== reqEnd) {
          this._emitPartial({
            requested: { start: reqStart, end: reqEnd },
            served: { start: servedStart, end: servedEnd },
            window: { start: windowMeta.start, end: windowMeta.end },
            at: Date.now(),
          });
        }
      }

      if (params.location_id) {
        appts = await offlineDb.appointments
          .where('[location_id+appointment_date]')
          .between([params.location_id, reqStart], [params.location_id, reqEnd], true, true)
          .toArray();
      } else {
        appts = await offlineDb.appointments
          .where('appointment_date')
          .between(reqStart, reqEnd, true, true)
          .toArray();
      }
    } else if (params.patient_id) {
      appts = await offlineDb.appointments.where('patient_id').equals(params.patient_id).toArray();
    } else if (params.case_id) {
      const cid = isNaN(params.case_id) ? params.case_id : parseInt(params.case_id, 10);
      appts = await offlineDb.appointments.filter(a => a.case_id == cid).toArray();
    } else {
      return null;
    }

    appts = appts.filter(a => a.status !== 'deleted');
    if (params.status) {
      const statuses = params.status.split(',');
      appts = appts.filter(a => statuses.includes(a.status));
    }
    if (params.appointment_type) {
      appts = appts.filter(a => a.appointment_type === params.appointment_type);
    }
    if (params.patient_id && params.start_date) {
      appts = appts.filter(a => a.patient_id === params.patient_id);
    }
    if (params.updated_after) {
      const since = Date.parse(params.updated_after);
      appts = appts.filter(a => (a.updated_at || 0) >= since);
    }

    // Stitch related lists by id from the cached feed tables.
    const uniq = (arr) => [...new Set(arr.filter(v => v != null))];
    const patientIds = uniq(appts.map(a => a.patient_id));
    const caseIds = uniq(appts.map(a => a.case_id));

    let patients = (await offlineDb.calendarPatients.bulkGet(patientIds)).filter(Boolean);
    // Fall back to full chart rows projected to the identity columns.
    const missingPatients = patientIds.filter(id => !patients.some(p => p.id === id));
    if (missingPatients.length) {
      const fullRows = (await offlineDb.patients.bulkGet(missingPatients)).filter(Boolean);
      patients = patients.concat(fullRows.map(p => ({
        id: p.id,
        first_name: p.first_name,
        middle_name: p.middle_name,
        last_name: p.last_name,
        birthday: p.birthday,
        insurance: p.insurance,
      })));
    }

    const cases = (await offlineDb.calendarCases.bulkGet(caseIds)).filter(Boolean);
    const referringIds = uniq(cases.map(c => c.referring_provider_id));
    const referring_providers = (await offlineDb.referringProviders.bulkGet(referringIds)).filter(Boolean);

    let users = await offlineDb.calendarUsers.toArray();
    if (!users.length) {
      users = (await offlineDb.organizationUsers.toArray()).map(u => ({
        id: u.id, first_name: u.first_name, middle_name: u.middle_name, last_name: u.last_name,
      }));
    }
    const appointment_types = await offlineDb.appointmentTypes.toArray();

    return { appointments: appts, patients, cases, referring_providers, users, appointment_types };
  }

  // Cached-identity patient search (name prefix tokens + DOB equality),
  // mirroring the server's prefix full-text behavior and paging shape.
  async _queryPatientSearch(params) {
    const perPage = parseInt(params.per_page, 10) || 25;
    const page = parseInt(params.page, 10) || 1;

    const tokens = (params.search_name || '')
      .toLowerCase()
      .split(/[\s,]+/)
      .filter(Boolean);
    const dob = params.search_dob ? normalizeDob(params.search_dob) : null;

    // Full chart rows (richer: address, phone, account number) take
    // precedence; calendar identity rows fill in everyone else on the
    // schedule. Every row gets a non-null `address` — PatientCard renders
    // `patient.address.city` unconditionally.
    const byId = new Map();
    for (const p of await offlineDb.patients.toArray()) {
      byId.set(p.id, { ...p, address: p.address || {} });
    }
    for (const p of await offlineDb.calendarPatients.toArray()) {
      if (!byId.has(p.id)) {
        byId.set(p.id, { ...p, address: {} });
      }
    }

    let matches = [...byId.values()].filter(p => {
      if (tokens.length) {
        const names = [p.first_name, p.middle_name, p.last_name]
          .filter(Boolean)
          .map(n => n.toLowerCase());
        const ok = tokens.every(t => names.some(n => n.startsWith(t)));
        if (!ok) return false;
      }
      if (dob) {
        if (normalizeDob(p.birthday) !== dob) return false;
      }
      return true;
    });

    matches.sort((a, b) =>
      (a.last_name || '').localeCompare(b.last_name || '') ||
      (a.first_name || '').localeCompare(b.first_name || '') ||
      String(a.id).localeCompare(String(b.id))
    );

    const itemsTotal = matches.length;
    const offset = (page - 1) * perPage;
    const items = matches.slice(offset, offset + perPage);
    const pageTotal = Math.max(1, Math.ceil(itemsTotal / perPage));

    return {
      items,
      itemsReceived: items.length,
      curPage: page,
      nextPage: page < pageTotal ? page + 1 : null,
      prevPage: page > 1 ? page - 1 : null,
      offset,
      perPage,
      itemsTotal,
      pageTotal,
    };
  }

  async _queryFlowSheetEntries(params) {
    let items;
    if (params.clinical_note_id) {
      items = await offlineDb.flowSheetEntries.where('clinical_note_id').equals(params.clinical_note_id).toArray();
    } else if (params.case_id != null && params.case_id !== '') {
      const cid = isNaN(params.case_id) ? params.case_id : parseInt(params.case_id, 10);
      items = await offlineDb.flowSheetEntries.filter(e => e.case_id == cid).toArray();
    } else if (params.patient_id) {
      items = await offlineDb.flowSheetEntries.where('patient_id').equals(params.patient_id).toArray();
    } else {
      return { items: [] };
    }

    if (params.patient_id && !params.clinical_note_id) {
      items = items.filter(e => e.patient_id === params.patient_id);
    }
    if (params.encounter_id) items = items.filter(e => e.encounter_id === params.encounter_id);
    if (params.flow_item_id) items = items.filter(e => e.flow_item_id === params.flow_item_id);
    if (params.exercise_id) items = items.filter(e => String(e.exercise_id) === String(params.exercise_id));
    if (params.data_type) items = items.filter(e => e.data_type === params.data_type);
    if (params.is_active !== undefined) {
      const active = params.is_active === 'true' || params.is_active === true;
      items = items.filter(e => (e.is_active !== false) === active);
    }
    if (params.exclude_discontinued === 'true') items = items.filter(e => !e.discontinued_at);
    if (params.unlinked === 'true') items = items.filter(e => !e.clinical_note_id && !e.encounter_id);
    if (params.entry_source) items = items.filter(e => e.entry_source === params.entry_source);
    if (params.standalone === 'true') {
      items = items.filter(e => !e.clinical_note_id && e.data_type === 'exercise' && e.entry_source !== 'quick_entry');
    }

    items.sort((a, b) => (b.recorded_at || 0) - (a.recorded_at || 0));

    const limit = params.limit ? parseInt(params.limit, 10) : items.length;
    const sliced = items.slice(0, limit);
    return { items: sliced, itemsTotal: items.length, curPage: 1, perPage: limit };
  }

  // DISTINCT ON (flow_item_id, side) by recorded_at desc, excluding
  // exercises and empty values — mirrors flow-sheet-entry-latest_GET.
  async _queryFlowSheetLatest(params) {
    let items;
    if (params.case_id != null && params.case_id !== '') {
      const cid = isNaN(params.case_id) ? params.case_id : parseInt(params.case_id, 10);
      items = await offlineDb.flowSheetEntries.filter(e => e.case_id == cid).toArray();
    } else if (params.patient_id) {
      items = await offlineDb.flowSheetEntries.where('patient_id').equals(params.patient_id).toArray();
    } else {
      return { items: [] };
    }

    items = items.filter(e =>
      e.is_active !== false &&
      (e.data_type || '') !== 'exercise' &&
      e.flow_item_id && e.value !== '' && e.value != null
    );

    const latest = new Map();
    for (const e of items) {
      const key = `${e.flow_item_id}|${e.side || ''}`;
      const prev = latest.get(key);
      if (!prev ||
          (e.recorded_at || 0) > (prev.recorded_at || 0) ||
          ((e.recorded_at || 0) === (prev.recorded_at || 0) && (e.created_at || 0) > (prev.created_at || 0))) {
        latest.set(key, e);
      }
    }

    return {
      items: [...latest.values()].map(e => ({
        id: e.id,
        flow_item_id: e.flow_item_id,
        side: e.side || '',
        label: e.label,
        data_type: e.data_type,
        unit: e.unit,
        value: e.value,
        value_json: e.value_json,
        numeric_value: e.numeric_value,
        recorded_at: e.recorded_at,
        encounter_id: e.encounter_id,
        clinical_note_id: e.clinical_note_id,
      })),
    };
  }

  async _queryFlowSheetCatalog(params) {
    let items = await offlineDb.flowSheetCatalog.toArray();
    if (params.organization_id) {
      items = items.filter(i => i.organization_id === params.organization_id || !i.organization_id);
    }
    if (params.category) items = items.filter(i => i.category === params.category);
    if (params.is_active !== undefined) {
      const active = params.is_active === 'true' || params.is_active === true;
      items = items.filter(i => (i.is_active !== false) === active);
    }
    const limit = params.limit ? parseInt(params.limit, 10) : items.length;
    return { items: items.slice(0, limit), itemsTotal: items.length };
  }

  async _queryClinicalNotes(params) {
    let results = offlineDb.clinicalNotes.toCollection();

    if (params.patient_id) {
      results = offlineDb.clinicalNotes.where('patient_id').equals(params.patient_id);
    } else if (params.case_id) {
      results = offlineDb.clinicalNotes.where('case_id').equals(
        isNaN(params.case_id) ? params.case_id : parseInt(params.case_id, 10)
      );
    } else if (params.encounter_id) {
      results = offlineDb.clinicalNotes.where('encounter_id').equals(params.encounter_id);
    }

    let items = await results.toArray();

    // Apply remaining JS-level filters
    if (params.case_id) {
      const cid = isNaN(params.case_id) ? params.case_id : parseInt(params.case_id, 10);
      items = items.filter(n => n.case_id == cid);
    }
    if (params.signed !== undefined) {
      const signed = params.signed === 'true' || params.signed === true;
      items = items.filter(n => Boolean(n.signed) === signed);
    }
    if (params.note_type) {
      items = items.filter(n => n.note_type === params.note_type);
    }
    if (params.provider_id) {
      items = items.filter(n => n.provider_id === params.provider_id);
    }

    items.sort((a, b) => (b.created_at || 0) - (a.created_at || 0));

    if (params.limit) {
      items = items.slice(0, parseInt(params.limit, 10));
    }

    return items;
  }

  async _queryEncounters(params) {
    let items;

    if (params.patient_id) {
      items = await offlineDb.encounters.where('patient_id').equals(params.patient_id).toArray();
    } else if (params.case_id) {
      items = await offlineDb.encounters.where('case_id').equals(
        isNaN(params.case_id) ? params.case_id : parseInt(params.case_id, 10)
      ).toArray();
    } else if (params.provider_id) {
      items = await offlineDb.encounters.where('provider_id').equals(params.provider_id).toArray();
    } else {
      items = await offlineDb.encounters.toArray();
    }

    if (params.case_id) {
      const cid = isNaN(params.case_id) ? params.case_id : parseInt(params.case_id, 10);
      items = items.filter(e => e.case_id == cid);
    }
    if (params.status) {
      const statuses = params.status.split(',');
      items = items.filter(e => statuses.includes(e.status));
    }
    if (params.start_date) {
      const start = new Date(params.start_date).getTime();
      items = items.filter(e => (e.encounter_date || e.created_at) >= start);
    }
    if (params.end_date) {
      const end = new Date(params.end_date).getTime();
      items = items.filter(e => (e.encounter_date || e.created_at) <= end);
    }

    items.sort((a, b) => (b.created_at || 0) - (a.created_at || 0));

    if (params.limit) {
      items = items.slice(0, parseInt(params.limit, 10));
    }

    return { items };
  }

  async _queryAppointments(params) {
    let items;

    if (params.provider_id && params.start_date) {
      items = await offlineDb.appointments
        .where('[provider_id+appointment_date]')
        .between(
          [params.provider_id, params.start_date],
          [params.provider_id, params.end_date || '￿'],
          true, true
        )
        .toArray();
    } else if (params.patient_id) {
      items = await offlineDb.appointments.where('patient_id').equals(params.patient_id).toArray();
    } else if (params.provider_id) {
      items = await offlineDb.appointments.where('provider_id').equals(params.provider_id).toArray();
    } else {
      items = await offlineDb.appointments.toArray();
    }

    if (params.status) {
      const statuses = params.status.split(',');
      items = items.filter(a => statuses.includes(a.status));
    }
    if (params.start_date && !params.provider_id) {
      items = items.filter(a => a.appointment_date >= params.start_date);
    }
    if (params.end_date) {
      items = items.filter(a => a.appointment_date <= params.end_date);
    }
    if (params.start_after) {
      items = items.filter(a => a.appointment_date >= params.start_after);
    }

    items.sort((a, b) => {
      const dateCompare = (a.appointment_date || '').localeCompare(b.appointment_date || '');
      if (dateCompare !== 0) return dateCompare;
      return (a.start_time || '').localeCompare(b.start_time || '');
    });

    if (params.limit) {
      items = items.slice(0, parseInt(params.limit, 10));
    }

    return items;
  }

  async _queryCases(params) {
    let items;

    if (params.patient_id && params.status) {
      items = await offlineDb.cases.where('[patient_id+status]').equals([params.patient_id, params.status]).toArray();
    } else if (params.patient_id) {
      items = await offlineDb.cases.where('patient_id').equals(params.patient_id).toArray();
    } else {
      items = await offlineDb.cases.toArray();
    }

    if (params.status && !params.patient_id) {
      items = items.filter(c => c.status === params.status);
    }
    if (params.care_setting) {
      items = items.filter(c => c.care_setting === params.care_setting);
    }

    if (params.limit) {
      items = items.slice(0, parseInt(params.limit, 10));
    }

    return { items };
  }

  async _queryPlansOfCare(params) {
    let items;

    if (params.case_id && params.status) {
      items = await offlineDb.plansOfCare.where('[case_id+status]').equals([
        isNaN(params.case_id) ? params.case_id : parseInt(params.case_id, 10),
        params.status,
      ]).toArray();
    } else if (params.case_id) {
      items = await offlineDb.plansOfCare.where('case_id').equals(
        isNaN(params.case_id) ? params.case_id : parseInt(params.case_id, 10)
      ).toArray();
    } else if (params.patient_id) {
      items = await offlineDb.plansOfCare.where('patient_id').equals(params.patient_id).toArray();
    } else {
      items = await offlineDb.plansOfCare.toArray();
    }

    if (params.status && !params.case_id) {
      items = items.filter(p => p.status === params.status);
    }

    if (params.limit) {
      items = items.slice(0, parseInt(params.limit, 10));
    }

    return items;
  }

  async _queryTemplates(params) {
    let items;
    if (params.organization_id) {
      items = await offlineDb.templates.where('organization_id').equals(params.organization_id).toArray();
    } else {
      items = await offlineDb.templates.toArray();
    }
    return items;
  }

  async _queryFormResponses(params) {
    const patientId = params.patient_identifier || params.patient_id;
    if (!patientId) return [];
    return offlineDb.formResponses.where('patient_id').equals(patientId).toArray();
  }

  async _queryFormReports(params) {
    if (!params.patient_id) return [];
    return offlineDb.formReports.where('patient_id').equals(params.patient_id).toArray();
  }

  async _queryOrganizationUsers(params) {
    if (!params.organization_id) return [];
    return offlineDb.organizationUsers.where('organization_id').equals(params.organization_id).toArray();
  }

  async _queryAppointmentTypes(params) {
    if (!params.organization_id) return [];
    return offlineDb.appointmentTypes.where('organization_id').equals(params.organization_id).toArray();
  }

  async _queryBillingRules(params) {
    if (!params.organization_id) return [];
    let items = await offlineDb.billingRules.where('organization_id').equals(params.organization_id).toArray();
    if (params.is_active !== undefined) {
      const active = params.is_active === 'true' || params.is_active === true;
      items = items.filter(r => r.is_active === active);
    }
    return items;
  }

  async _queryInterventionCatalog(params) {
    let items = await offlineDb.interventionCatalog.toArray();
    if (params.is_active !== undefined) {
      const active = params.is_active === 'true' || params.is_active === true;
      items = items.filter(i => i.is_active === active);
    }
    if (params.category) {
      items = items.filter(i => i.category === params.category);
    }
    if (params.clinical_specialty) {
      items = items.filter(i => i.clinical_specialty === params.clinical_specialty);
    }
    if (params.limit) {
      items = items.slice(0, parseInt(params.limit, 10));
    }
    return items;
  }
}

export const cacheReader = new CacheReader();
